import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { Search, MapPin } from "lucide-react";

const statuses = ["received", "quoted", "in_progress", "ready", "picked_up", "cancelled"];
const shops = ["Rhodes", "Ashfield", "West Ryde", "Charlestown"];

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({});
  const [edits, setEdits] = useState({});
  const [selected, setSelected] = useState(null);
  const [shopFilter, setShopFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [{ data: b }, { data: s }] = await Promise.all([api.get("/bookings"), api.get("/admin/stats")]);
      setBookings(b); setStats(s);
    } catch { toast.error("Could not load bookings"); }
  };
  useEffect(() => { load(); }, []);

  // Per-shop counts
  const shopCounts = useMemo(() => {
    const m = { all: bookings.length };
    shops.forEach(s => { m[s] = 0; });
    bookings.forEach(b => { if (m[b.location] != null) m[b.location]++; });
    return m;
  }, [bookings]);

  // Filtered rows
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return bookings.filter(b => {
      if (shopFilter !== "all" && b.location !== shopFilter) return false;
      if (statusFilter !== "all" && b.status !== statusFilter) return false;
      if (!needle) return true;
      return (
        b.code?.toLowerCase().includes(needle) ||
        b.customer_name?.toLowerCase().includes(needle) ||
        b.customer_phone?.toLowerCase().includes(needle) ||
        b.customer_email?.toLowerCase().includes(needle) ||
        b.service_type?.toLowerCase().includes(needle)
      );
    });
  }, [bookings, shopFilter, statusFilter, q]);

  const save = async (id) => {
    const e = edits[id] || {};
    try {
      await api.patch(`/bookings/${id}`, {
        status: e.status || bookings.find(x => x.id === id).status,
        deposit_amount_aud: e.deposit_amount_aud !== undefined ? parseFloat(e.deposit_amount_aud) : null,
        admin_note: e.admin_note || "",
      });
      toast.success("Updated · Customer notified via WhatsApp (mocked)");
      setEdits({ ...edits, [id]: undefined });
      await load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="p-8 md:p-12">
      <div className="overline">Manager · Dashboard</div>
      <h1 className="font-serif-display text-4xl mt-2">All Bookings</h1>
      <p className="text-[#4A4A4A] mt-2 text-sm">Master list across all four Sydney shops. Filter by shop or search by name, phone, code or service.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        {[
          { label: "Total", value: stats.total || 0 },
          { label: "Today", value: stats.today || 0 },
          { label: "In progress", value: stats.pending || 0 },
          { label: "Ready for pickup", value: stats.ready_for_pickup || 0 },
        ].map(s => (
          <div key={s.label} className="border border-[#E2E8F0] bg-white p-6">
            <div className="overline">{s.label}</div>
            <div className="font-serif-display text-4xl mt-2">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Shop filter chips */}
      <div className="mt-10 flex flex-wrap items-center gap-2">
        <span className="overline mr-2 flex items-center gap-1.5"><MapPin size={12}/> Shop</span>
        {[{ key: "all", label: "All shops" }, ...shops.map(s => ({ key: s, label: s }))].map(({ key, label }) => (
          <button key={key} onClick={() => setShopFilter(key)}
                  data-testid={`filter-shop-${key.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${shopFilter === key ? "bg-[#0F172A] text-[#FFFFFF] border-[#0F172A]" : "border-[#E2E8F0] hover:border-[#0F172A]"}`}>
            {label}
            <span className={`ml-2 text-[10px] ${shopFilter === key ? "text-[#D4AF37]" : "text-[#4A4A4A]"}`}>
              {shopCounts[key] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Status + search */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="filter-status"
                className="border border-[#E2E8F0] focus:border-[#0F172A] bg-white px-3 py-2 outline-none text-sm">
          <option value="all">All statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <div className="flex items-center border border-[#E2E8F0] focus-within:border-[#0F172A] bg-white px-3 py-2 flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="text-[#4A4A4A]"/>
          <input data-testid="filter-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search name, phone, email, code or service"
                 className="flex-1 ml-2 outline-none text-sm bg-transparent"/>
        </div>
        <span className="text-sm text-[#4A4A4A]" data-testid="filter-result-count">
          {filtered.length} of {bookings.length}
        </span>
      </div>

      <div className="mt-6 border border-[#E2E8F0] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <tr className="text-left">
              <th className="p-4">Code</th>
              <th className="p-4">Customer</th>
              <th className="p-4">Service</th>
              <th className="p-4">Date</th>
              <th className="p-4">Shop</th>
              <th className="p-4">Deposit (AUD)</th>
              <th className="p-4">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-[#4A4A4A]">No bookings match these filters.</td></tr>
            )}
            {filtered.map(b => {
              const e = edits[b.id] || {};
              return (
                <tr key={b.id} data-testid={ADMIN.bookingRow(b.id)} className="border-b border-[#E2E8F0]/60 hover:bg-[#F8FAFC]/60">
                  <td className="p-4 font-mono">{b.code}</td>
                  <td className="p-4">
                    <div>{b.customer_name}</div>
                    <div className="text-xs text-[#4A4A4A]">{b.customer_phone}</div>
                  </td>
                  <td className="p-4 capitalize">{b.service_type}</td>
                  <td className="p-4">{b.preferred_date}<br/><span className="text-xs text-[#4A4A4A]">{b.preferred_time}</span></td>
                  <td className="p-4">{b.location}</td>
                  <td className="p-4">
                    <input data-testid={ADMIN.depositInput(b.id)} type="number" step="0.01"
                           defaultValue={b.deposit_amount_aud || ""}
                           onChange={(ev) => setEdits({ ...edits, [b.id]: { ...e, deposit_amount_aud: ev.target.value } })}
                           className="w-24 border border-[#E2E8F0] focus:border-[#0F172A] bg-white p-2 outline-none"/>
                    {b.deposit_paid && <div className="text-xs text-[#0F172A] mt-1">PAID</div>}
                  </td>
                  <td className="p-4">
                    <select data-testid={ADMIN.statusSelect(b.id)} defaultValue={b.status}
                            onChange={(ev) => setEdits({ ...edits, [b.id]: { ...e, status: ev.target.value } })}
                            className="border border-[#E2E8F0] focus:border-[#0F172A] bg-white p-2 outline-none">
                      {statuses.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                  <td className="p-4 flex gap-2">
                    <button onClick={() => setSelected(b)} className="text-xs uppercase tracking-widest link-underline">View</button>
                    <button data-testid={ADMIN.saveBookingBtn(b.id)} onClick={() => save(b.id)}
                            className="text-xs uppercase tracking-widest bg-[#0F172A] text-[#FFFFFF] px-3 py-2 hover:bg-[#B8941F]">
                      Save
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto p-8" data-testid="booking-detail-modal">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="overline">Booking</div>
                <div className="font-serif-display text-3xl">{selected.code}</div>
                <div className="text-xs text-[#4A4A4A] mt-1 uppercase tracking-widest">{selected.location} · {selected.service_type}</div>
              </div>
              <span className={`text-xs uppercase tracking-widest px-3 py-1 ${selected.status === "ready" ? "bg-[#0F172A] text-[#FFFFFF]" : "bg-[#F8FAFC]"}`}>
                {selected.status?.replace("_", " ")}
              </span>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="overline">Customer</div>
                <div className="mt-1 font-medium">{selected.customer_name}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={`tel:${selected.customer_phone}`}
                     className="text-xs uppercase tracking-widest px-3 py-1.5 border border-[#0F172A] hover:bg-[#0F172A] hover:text-[#FFFFFF]">
                    Call {selected.customer_phone}
                  </a>
                  <a href={`https://wa.me/${(selected.customer_phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                     className="text-xs uppercase tracking-widest px-3 py-1.5 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0F172A]">
                    WhatsApp
                  </a>
                  <a href={`mailto:${selected.customer_email}`}
                     className="text-xs uppercase tracking-widest px-3 py-1.5 border border-[#E2E8F0] hover:border-[#0F172A]">
                    Email
                  </a>
                </div>
                <div className="text-xs text-[#4A4A4A] mt-3">{selected.customer_email}</div>
              </div>
              <div>
                <div className="overline">Appointment</div>
                <div className="mt-1">{selected.preferred_date} · {selected.preferred_time}</div>
                <div className="text-[#4A4A4A]">{selected.location} shop</div>
                <div className="overline mt-4">Payments</div>
                <div className="mt-1">
                  Booking fee: A${(selected.booking_fee_aud || 0).toFixed(2)} {selected.booking_fee_paid ? "· PAID" : "· pending"}
                </div>
                <div>
                  Deposit: {selected.deposit_amount_aud ? `A$${selected.deposit_amount_aud.toFixed(2)}` : "—"} {selected.deposit_paid ? "· PAID" : ""}
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="overline">Notes</div>
                <div className="mt-1">{selected.service_notes || "—"}</div>
              </div>
            </div>

            {selected.photos?.length > 0 && (
              <div className="mt-6">
                <div className="overline">Photos</div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {selected.photos.map((p, i) => <img key={i} src={p} alt="garment" className="aspect-square object-cover"/>)}
                </div>
              </div>
            )}

            {selected.history?.length > 0 && (
              <div className="mt-6">
                <div className="overline">Timeline</div>
                <ol className="mt-2 space-y-2">
                  {selected.history.map((h, i) => (
                    <li key={i} className="flex gap-3 text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0F172A] mt-1.5"/>
                      <div>
                        <div className="font-medium">{h.status?.replace("_", " ")}</div>
                        <div className="text-[#4A4A4A]">{new Date(h.at).toLocaleString()}</div>
                        {h.note && <div className="text-[#4A4A4A] italic mt-0.5">{h.note}</div>}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <button onClick={() => setSelected(null)} className="mt-8 px-6 py-3 bg-[#0F172A] text-[#FFFFFF] text-xs uppercase tracking-[0.25em]">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
